import React, { useState, useEffect } from 'react';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TextField, FormHelperText } from '@mui/material';
import { useDebounce } from '@fuse/hooks';
import { comapreDates, getFormattedDate } from '@fuse/utils/dateOperations';
import { convertProperDateFormat } from 'helper/common';

const CustomDatePickerV2 = ({
    state,
    setState,
    label,
    required,
    minDate,
    maxDate,
    onChangeFromParent = () => { },
}) => {
    const [inputValue, setInputValue] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [values, setValues] = useState(null);
    const [onMounted, setOnMount] = useState(false);

    const handleStateChange = ({ key, val }) => {
        setState((prev) => ({ ...prev, [key]: val }));
    };

    useEffect(() => {
        if ((typeof state === 'string' ? state : state?.date) && !onMounted) {
            const dateValue = convertProperDateFormat(state?.date ? state?.date : state);
            setValues(new Date(dateValue));
            setOnMount(true);
        } else {
            setValues(null);
        }
    }, []);

    function onKeyPressValidaion(e) {
        try {
            // const regex = /(0[1-9]|1[0-2])[/](0[1-9]|[12][0-9]|3[01])[/](19|20)\d{2}/
            const regex1 = /(0[13578]|1[02])[/](0[1-9]|[12][0-9]|3[01])[/](19|20)\d{2}/
            const regex2 = /(0[469]|1[1])[/](0[1-9]|[12][0-9]|3[0])[/](19|20)\d{2}/
            const regex3 = /(0[2])[/](0[1-9]|[12][0-9])[/](19|20)\d{2}/

            const value = inputValue;

            if (!value.length) return
            if (value.includes('_')) {
                handleStateChange({
                    key: 'error',
                    val: true
                })
                setErrorMessage('Provide a valid date')
                return
            }
            handleStateChange({
                key: 'error',
                val: false
            })
            setErrorMessage('')


            // Check if date is valid or not
            // regex.test(value) return false if string dosen't match with regex
            if (!regex1.test(value) && !regex2.test(value) && !regex3.test(value)) {
                handleStateChange({
                    key: 'error',
                    val: true
                })
                setErrorMessage('Provide a valid date')
                return
            }
            handleStateChange({
                key: 'error',
                val: false
            })
            setErrorMessage('')


            // Check if date lesser than minDate
            if (minDate || null) {
                const { hasError: lessThanVaildationFailed,
                    message: lessThanVaildationFailedMessage
                } = comapreDates(minDate, value, 'is_less_than', 'Date should not be before minimal date');
                if (lessThanVaildationFailed) {
                    handleStateChange({
                        key: 'error',
                        val: true
                    })
                    setErrorMessage(lessThanVaildationFailedMessage)
                    return
                }
                handleStateChange({
                    key: 'error',
                    val: false
                })
                setErrorMessage(lessThanVaildationFailedMessage)

            }

            // Check if date greater than maxDate
            if (maxDate || null) {
                const {
                    hasError: greaterThanVaildationFailed,
                    message: greaterThanVaildationFailedMessage
                } = comapreDates(maxDate, value, 'is_greater_than', 'Date should not be after maximal date');
                if (greaterThanVaildationFailed) {
                    handleStateChange({
                        key: 'error',
                        val: true
                    })
                    setErrorMessage(greaterThanVaildationFailedMessage)
                    return
                }
                handleStateChange({
                    key: 'error',
                    val: false
                })
                setErrorMessage(greaterThanVaildationFailedMessage)

            }
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        if (!inputValue.length) {
            handleStateChange({
                key: 'error',
                val: false
            })
            setErrorMessage('')
            return
        }
        onKeyPressValidaion(inputValue);
    }, [inputValue])

    const debounceFetch = useDebounce((event) => {
        onChangeFromParent(event)
    }, 100)

    return (
        <div style={{ width: '100%', marginTop: '3px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                    views={['month', 'day', 'year']}
                    inputFormat="MM/dd/yyyy"
                    name="date"
                    label={label || ''}
                    value={typeof state === 'string' ? values : state?.date ? new Date(state?.date) : null}
                    onChange={(event) => {
                        if (isNaN(event)) return
                        if (!event) {
                            handleStateChange({
                                key: 'date',
                                val: null
                            })
                            return
                        }
                        event.setHours(new Date().getHours())
                        event.setMinutes(new Date().getMinutes())
                        event.setSeconds(new Date().getSeconds())
                        handleStateChange({
                            key: 'date',
                            // val: ((new Date(event).toISOString()).split('T'))[0]
                            val: event
                        })
                        setInputValue(getFormattedDate((new Date(event).toISOString()).split('T')[0]))
                        debounceFetch(event)
                    }}
                    minDate={minDate || null}
                    maxDate={maxDate || null}
                     onError={(reason) => {
                        if (reason === 'invalidDate') {
                            handleStateChange({ key: 'error', val: true });
                            setErrorMessage('Provide a valid date');
                        } else {
                            handleStateChange({ key: 'error', val: false });
                            setErrorMessage('');
                        }
                    }}
                    renderInput={(params) => {
                        const InputProps = {
                            ...params.InputProps,
                            disableUnderline: true,
                        };

                        return (
                            <TextField
                                {...params}
                                variant="standard"
                                fullWidth
                                size="small"
                                required={required || false}
                                error={!!state.error}
                                helperText={errorMessage}
                                InputProps={InputProps}
                            />
                        );
                    }}

                    PopoverProps={{ disableRestoreFocus: true }}
                />
            </LocalizationProvider>
            {errorMessage && (
                <FormHelperText sx={{ color: 'red', fontSize: '12px', marginLeft: '2px' }}>
                    {errorMessage}
                </FormHelperText>
            )}
        </div>
    );
};

export default CustomDatePickerV2;
